import { useEffect, useState } from 'react';

// Keep a table's header fixed while its rows scroll (expenses pattern): the
// table body scrolls internally via antd `scroll.y`, sized from the VIEWPORT
// (wrapper top → bottom of window) so it stays bounded however many rows there
// are. `reserve` = px below the body that must stay in view — the table's own
// header + pagination chrome, any summary bar under it, and a bottom gutter.
//
// Returns a callback ref for the wrapper and the `scroll.y` to pass — or
// undefined on phones, where the whole page scrolls instead, and when the rows
// fit anyway: antd's scroll.y forces `overflow-y: scroll`, so a short table
// would otherwise show an empty grey scrollbar track down its side.
//
// ⚠ scroll.y needs a NUMERIC scroll.x (sum of column widths): with
// 'max-content' the separately-rendered header drifts out of line with the body.
export function useTableScrollY(reserve: number) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [y, setY] = useState(400);
  const [overflows, setOverflows] = useState(true);
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(() => update());
    let tbody: Element | null = null;
    const update = () => {
      const top = el.getBoundingClientRect().top;
      const avail = Math.max(160, window.innerHeight - top - reserve);
      setY(avail);
      // tbody keeps the same height in both modes, so this can't oscillate.
      const tb = el.querySelector('.ant-table-tbody');
      if (tb !== tbody) {
        if (tbody) ro.unobserve(tbody);
        if (tb) ro.observe(tb);
        tbody = tb;
      }
      setOverflows(!tb || tb.getBoundingClientRect().height > avail);
    };
    update();
    ro.observe(el);
    // The table (and its tbody) mounts after the wrapper — e.g. once loading ends.
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [el, reserve]);
  const mobile = typeof window !== 'undefined' && window.innerWidth < 640;
  return [setEl, mobile || !overflows ? undefined : y] as const;
}
